import {FC, useState, useEffect, useMemo} from 'react'
import {PageTitle} from '../../../../_metronic/layout/core'
import {Card, Row, Col, Form, Button, Spinner, OverlayTrigger, Tooltip} from 'react-bootstrap'
import {
  createAgreement,
  deleteAgreementChapter,
  getAgreementById,
  getOptionAgreements,
  updateAgreementById,
  exportAgreementDetail,
  submitReorderChapter,
  verifySourceUrl,
} from './core/_requests'

import useAxiosPrivate from '../../../libs/useAxiosPrivate'
import {Link, useNavigate, useParams} from 'react-router-dom'
import {AgreementDetail, AgreementSource} from './core/_models'
import {
  buildAgreementFormEntries,
  getErrorMessage,
  parseSources,
  deriveFieldsFromParties,
  WORLD_PARTY,
} from './core/_transforms'
import {AgreementChapterNew} from '../trade-data-agreement-chapter-new/core/_models'
import './TradeDataAgreementDetailNewPage.scss'
import Select, {MultiValue} from 'react-select'
import {useAbility} from '@casl/react'
import {AbilityContext} from '../../../modules/auth/core/AbilityContext'
import {MRT_ColumnDef} from 'material-react-table'
import {Box} from '@mui/material'
import ReorderableDataTable from '../../../../ui-kit/ReorderableDataTable/ReorderableDataTable'
import DataTable from '../../../../ui-kit/DataTable'
import {ColumnDef, createColumnHelper} from '@tanstack/react-table'
import AgreementDetailNewView from './components/AgreementDetailNewView'
import ModalAddSources from './components/ModalAddSources'
import ModalAuditLog from '../activity-logs/components/ModalAuditLog'
import {showSwal} from '../../../../ui-kit'
import {WTO_AGREEMENT_TYPE} from '../../../libs/constant'

const confirmDialog = (title: string) => {
  return showSwal({
    title,
    icon: 'question',
    showCloseButton: true,
    showCancelButton: true,
    confirmButtonText: 'Yes',
    cancelButtonText: 'No',
  })
}

// Paired with the .resizable-multiselect wrapper class: container/control
// stretch to fill whatever height the user drags the wrapper to (instead of
// staying at their own natural height and leaving dead space below the
// chips), and valueContainer scrolls internally once its content is taller
// than that dragged height.
const multiSelectResizableStyles = {
  container: (base: any) => ({...base, flex: 1, minHeight: 0}),
  control: (base: any) => ({...base, height: '100%'}),
  valueContainer: (base: any) => ({...base, flexWrap: 'wrap', overflowY: 'auto'}),
}

const TradeDataAgreementDetailNewPage: FC<{subject: string}> = ({subject}) => {
  const ability = useAbility(AbilityContext)
  const [internalLoading, setInternalLoading] = useState(false)
  const axiosPrivate = useAxiosPrivate()
  const navigate = useNavigate()
  const {idAgreement} = useParams()
  const isDetailMode = Boolean(idAgreement)
  const isAddMode = !idAgreement
  const [isEditMode, setIsEditMode] = useState(false)
  const [isReorderMode, setIsReorderMode] = useState(false)
  const [isReordered, setIsReordered] = useState(false)
  const [reorderChapters, setReorderChapters] = useState<AgreementChapterNew[]>([])
  const [isFormValid, setIsFormValid] = useState(false)
  const [agreementStatus, setAgreementStatus] = useState([])
  const [agreementType, setAgreementType] = useState([])
  const [agreementNotification, setAgreementNotification] = useState([])
  const [agreementComposition, setAgreementComposition] = useState([])
  const [agreementDocumentType, setAgreementDocumentType] = useState([])
  const [countries, setCountries] = useState<any[]>([])

  const [importFile, setImportFile] = useState<File | null>(null)
  const [agreementDetail, setAgreementDetail] = useState<AgreementDetail>({
    acronym: '',
    agreement: '',
    type: [],
    wto_rta_id: '',
    status: '',
    notification: [],
    date_signed: '',
    date_into_force: '',
    date_notification: '',
    end_implementation: '',
    date_inactive: '',
    composition: '',
    subregion: '',
    parties_wto: '',
    crossregional: '',
    language: '',
    sources: '',
    date_xml_created: '',
    date_xml_edited: '',
    xml_file: '',
    xml_edited_comments: '',
    text_available: '',
    document_type: null,
    parties_escap: '',
    services_schedules: '',
    desta_id: '',
    iso: [], //refer to parties
    original_id: 0,
  })
  // `region` is a purely client-computed display value derived from the
  // selected party countries — it is never stored/round-tripped by the
  // backend, so it lives in local state rather than on `agreementDetail`.
  const [region, setRegion] = useState('')
  const [pendingChapters, setPendingChapters] = useState([])
  const [pendingAgreement, setPendingAgreement] = useState<any>([])

  // sources
  const [showSourceModal, setShowSourceModal] = useState(false)
  const [showAuditLogModal, setShowAuditLogModal] = useState(false)
  const [sources, setSources] = useState<AgreementSource[]>([])
  const [sourceAlias, setSourceAlias] = useState('')
  const [sourceLink, setSourceLink] = useState('')
  const [isSourceLinkVerified, setIsSourceLinkVerified] = useState<boolean | null>(null)

  const sourceColumnHelper = createColumnHelper<AgreementSource>()

  const handleVerifySourceUrl = async (url: string) => {
    try {
      const response = await verifySourceUrl(axiosPrivate, url)
      setIsSourceLinkVerified(response.data.isVerified === true || response.data.isVerified === 1)
    } catch (err) {
      console.error(err)
      setIsSourceLinkVerified(false)
      showSwal('Error', getErrorMessage(err, 'Failed to verify source URL'), 'error')
    }
  }

  const handleAddSource = () => {
    if (!sourceAlias.trim() || !sourceLink.trim()) return

    setSources((prev) => [
      ...prev,
      {
        id: `${Date.now()}_${Math.random()}`, // collision-resistant local id
        alias: sourceAlias.trim(),
        link: sourceLink.trim(),
        is_source_valid: isSourceLinkVerified ? '1' : '0',
      },
    ])

    // reset input
    setSourceAlias('')
    setSourceLink('')
    setIsSourceLinkVerified(null)
  }

  const handleSourceLinkChange = (value: string) => {
    setSourceLink(value)
    setIsSourceLinkVerified(null)

    // If typing a link, must verify it
    setAgreementDetail((prev) => ({
      ...prev,
      is_sources_valid: value.trim() === '',
    }))
  }

  const handleDeleteSource = (id: string) => {
    setSources((prev) => prev.filter((item) => item.id !== id))
  }

  const sourceColumns: ColumnDef<AgreementSource, any>[] = useMemo(
    () => [
      sourceColumnHelper.accessor('alias', {
        header: 'Alias',
        cell: (info) => info.getValue() || '-',
        meta: {
          headerClass: 'text-left',
          cellClass: 'text-left fw-semibold',
        },
        enableSorting: true,
        enableGlobalFilter: true,
      }),

      sourceColumnHelper.accessor('link', {
        header: 'Link',
        cell: (info) => {
          const value = info.getValue()
          return value ? (
            <a href={value} target='_blank' rel='noreferrer' className='text-link me-2'>
              {value}&nbsp;
              {info.row.original.is_source_valid !== undefined && (
                <i
                  className={`fa-solid ${
                    info.row.original.is_source_valid === '1'
                      ? 'fa-check text-success'
                      : 'fa-xmark text-danger'
                  }`}
                ></i>
              )}
            </a>
          ) : (
            '-'
          )
        },
        meta: {
          headerClass: 'text-left',
          cellClass: 'text-left text-break',
        },
        enableSorting: false,
        enableGlobalFilter: true,
      }),

      sourceColumnHelper.accessor('id', {
        header: '',
        cell: (info) => {
          const id = info.getValue()

          return isEditMode ? (
            <Link to='#' className='link-danger' onClick={() => handleDeleteSource(id)}>
              <span className='fa fa-trash'></span>
            </Link>
          ) : (
            <a href={sources.find((s) => s.id === id)?.link} target='_blank' rel='noreferrer'>
              <span className='fa fa-eye'></span>
            </a>
          )
        },
        meta: {
          headerClass: 'text-end',
          cellClass: 'text-end',
        },
        enableSorting: false,
        enableGlobalFilter: false,
      }),
    ],
    [isEditMode, sources]
  )

  // table chapters
  const [dataTable, setDataTable] = useState<AgreementChapterNew[]>([])

  const columns = useMemo<MRT_ColumnDef<AgreementChapterNew>[]>(() => {
    const cols: MRT_ColumnDef<AgreementChapterNew>[] = [
      {
        accessorKey: 'label',
        header: 'Label',
        Cell: ({cell, row}) => {
          return (
            <Box
              component='span'
              sx={{
                paddingLeft: `${row.depth * 2.5}rem`,
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
              }}
            >
              {row.getCanExpand() ? (
                <i
                  className={`fa-solid cursor-pointer text-muted ${
                    row.getIsExpanded() ? 'fa-sort-down' : 'fa-caret-right'
                  }`}
                  style={{width: '10px', textAlign: 'center'}}
                  onClick={(e) => {
                    e.stopPropagation()
                    row.toggleExpanded()
                  }}
                />
              ) : (
                <span style={{width: '10px'}} />
              )}
              {cell.getValue<string>() ?? '-'}
            </Box>
          )
        },
      },
      {
        accessorKey: 'title',
        header: 'Title',
        Cell: ({cell, row}) => {
          return (
            <Box
              component='span'
              sx={{
                paddingLeft: `${row.depth * 2.5}rem`,
              }}
            >
              {cell.getValue<string>() ?? '-'}
            </Box>
          )
        },
      },

      {
        accessorKey: 'type',
        header: 'Section Type',
        Cell: ({cell, row}) => {
          return (
            <Box component='span' sx={{paddingLeft: `${row.depth * 2}rem`}}>
              {cell.getValue<string>() ?? '-'}
            </Box>
          )
        },
      },
      {
        accessorKey: 'nwords_new',
        header: 'N Words',
        Cell: ({cell}) => {
          return (
            <Box sx={{textAlign: 'right', display: 'block'}} component='span'>
              {cell.getValue<number>() ?? 0}
            </Box>
          )
        },
      },
      {
        accessorKey: 'sort_order',
        header: 'Order',
        Cell: ({cell}) => {
          return (
            <Box sx={{textAlign: 'right', display: 'block'}} component='span'>
              {cell.getValue<number>() ?? 0}
            </Box>
          )
        },
      },
    ]

    if (!isReorderMode && (ability.can('delete', subject) || ability.can('update', subject))) {
      cols.push({
        accessorKey: 'id',
        header: 'Actions',
        Cell: ({cell, row}) => {
          const hasChildren = row.original.children && row.original.children.length > 0
          return (
            <div className='d-flex justify-content-center align-items-center'>
              {ability.can('update', subject) ? (
                <Link
                  to={`/new-rta/chapter/${cell.getValue()}/edit`}
                  style={{pointerEvents: pendingChapters.length > 0 ? 'none' : 'auto'}}
                >
                  <span className='fa fa-edit text-warning'></span>
                </Link>
              ) : (
                ''
              )}
              {ability.can('delete', subject) ? (
                <OverlayTrigger
                  placement='top'
                  overlay={
                    hasChildren ? (
                      <Tooltip id={`delete-disabled-tooltip-${cell.getValue()}`}>
                        Cannot delete while there are child contents referring to this item
                      </Tooltip>
                    ) : (
                      <></>
                    )
                  }
                >
                  <span className='d-inline-block'>
                    <Button
                      className='btn btn-sm align-items-center'
                      onClick={() => handleAgreementChapterDeletion(cell.getValue())}
                      disabled={hasChildren}
                      style={{
                        pointerEvents: hasChildren ? 'none' : 'auto',
                        backgroundColor: 'transparent',
                        border: 'transparent',
                      }}
                    >
                      <span className='fa fa-trash text-danger'></span>
                    </Button>
                  </span>
                </OverlayTrigger>
              ) : (
                ''
              )}
            </div>
          )
        },
      })
    }

    return cols
  }, [isReorderMode, pendingChapters, ability, subject]) // Re-run when reorder mode, pending-review lock, or permissions change
  // end of table chapters

  const removeAndShiftOrder = (
    items: any[],
    targetId: string | number
  ): {newItems: any[]; foundMetadata: any} => {
    let foundMetadata = {
      type: '',
      sort_order: 0,
    }

    // 1. First, search the current level array to see if the target exists here
    const targetIndex = items.findIndex((item) => item.id === targetId)

    if (targetIndex !== -1) {
      // We found it! Capture its properties so we know what to shift
      const targetItem = items[targetIndex]
      foundMetadata = {
        type: targetItem.type,
        sort_order: targetItem.sort_order,
      }

      // Filter out the target item, and map over the rest to shift their sort_order if needed
      const newItems = items
        .filter((item) => item.id !== targetId)
        .map((item) => {
          if (item.type === foundMetadata.type && item.sort_order > foundMetadata.sort_order) {
            return {...item, sort_order: item.sort_order - 1}
          }
          return item
        })

      return {newItems, foundMetadata}
    }

    // 2. If it's not at this level, recursively search children
    const newItems = items.map((item) => {
      if (item.children && item.children.length > 0) {
        const result = removeAndShiftOrder(item.children, targetId)

        // If the item was found down this branch, update foundMetadata to ripple back up
        if (result.foundMetadata) {
          foundMetadata = result.foundMetadata
          return {...item, children: result.newItems}
        }
      }
      return item
    })

    // 3. Since the target could theoretically be found deeper in the tree,
    // we still need to check if we need to shift things at THIS level based on the foundMetadata
    if (foundMetadata) {
      const shiftedItems = newItems.map((item) => {
        // NOTE: Assuming `type` grouping spans across the whole tree (or at least this level)
        if (item.type === foundMetadata.type && item.sort_order > foundMetadata.sort_order) {
          return {...item, sort_order: item.sort_order - 1}
        }
        return item
      })
      return {newItems: shiftedItems, foundMetadata}
    }

    // Not found anywhere
    return {newItems, foundMetadata}
  }

  const handleAgreementChapterDeletion = (id) => {
    confirmDialog('Are you sure you want to delete this chapter?').then((res) => {
      if (!res.isConfirmed) return

      deleteAgreementChapter(axiosPrivate, id)
        .then((res) => {
          if (res.status === 200) {
            showSwal({
              title: 'Agreement Content deleted',
              icon: 'success',
              confirmButtonText: 'OK',
            }).then(() => {
              setDataTable((prev) => {
                const {newItems} = removeAndShiftOrder(prev, id)
                return newItems
              })
            })
          }
        })
        .catch((err) => {
          console.log(err)
          showSwal('Error', getErrorMessage(err, 'Failed to delete Agreement Chapter.'))
        })
    })
  }

  const fetchOptionAgreement = async () => {
    try {
      setInternalLoading(true)

      const optionsRes = await getOptionAgreements(axiosPrivate)
      const resData = optionsRes.data.data
      setAgreementStatus(resData.status)
      setAgreementType(resData.type.map((n: string) => ({value: n, label: n})))
      setAgreementNotification(resData.notification.map((n: string) => ({value: n, label: n})))
      setAgreementComposition(resData.composition)
      setAgreementDocumentType(resData.document_type)
      const allCountries = [
        {
          value: 'EUN',
          label: 'European Union (EUN)',
          region_un: ['Europe'],
          subregion_un: [],
          escap: false,
        },
        {
          value: 'TWN',
          label: 'Other Asia (TWN)',
          region_un: ['Asia Pacific'],
          subregion_un: [],
          escap: false,
        },
        ...resData.parties, // ← backend must return this
      ]
      setCountries(allCountries)

      if (isDetailMode) {
        const detailRes = await getAgreementById(axiosPrivate, {idAgreement})
        const detailResdata = detailRes.data.data
        if (detailResdata) {
          const agreementDetailType = detailResdata.type ? detailResdata.type.split(';') : []

          const agreementDetailNotification = detailResdata.notification
            ? detailResdata.notification.split(';')
            : []

          const agreementDetailParties = detailResdata.iso ? detailResdata.iso.split(';') : []
          const agreementDetailSources = parseSources(
            detailResdata.sources,
            detailResdata.is_sources_valid
          )

          const trimmedParties = agreementDetailParties.map((iso: string) => iso.trim())
          const derived = deriveFieldsFromParties(trimmedParties, allCountries)

          // Older agreements were saved before `subregion` existed — backfill it
          // from the stored parties list so it still displays.
          let subregion = detailResdata.subregion
          if (!subregion && agreementDetailParties.length) {
            subregion = derived.subregionArray.join('; ')
          }

          // `region` is a purely client-computed display value (never stored or
          // round-tripped by the backend) — always recompute it fresh from the
          // current party list rather than backfilling only when blank.
          const derivedRegion = derived.regionArray.join('; ')

          setAgreementDetail((prev) => ({
            ...prev,
            ...detailResdata,
            type: agreementDetailType,
            notification: agreementDetailNotification,
            iso: agreementDetailParties,
            sources: agreementDetailSources,
            subregion,
            // Parties WTO is now a fully derived, disabled field (item 5) —
            // always recompute from the current party list rather than
            // trusting whatever was last stored.
            parties_wto: agreementDetailParties.length ? derived.partiesWto : '',
          }))
          setRegion(derivedRegion)
          setSources(agreementDetailSources)
          setDataTable(detailResdata.chapters)
          setPendingChapters(detailResdata.pending_chapters)
          setPendingAgreement(detailResdata.pending_agreement)
          // should be add the optionPartiesHere
        }
      }
    } catch (error) {
      console.error(error)
    } finally {
      setInternalLoading(false)
    }
  }
  const isEditDisabled = pendingChapters.length > 0 || pendingAgreement.length > 0
  const isExportDisabled = pendingChapters.length > 0 || pendingAgreement.length > 0

  useEffect(() => {
    fetchOptionAgreement()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idAgreement])

  // Add or Edit
  const handleAgreementChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const {name, value, multiple, selectedOptions} = e.target as HTMLSelectElement

    if (multiple) {
      const values = Array.from(selectedOptions, (option) => option.value)
      setAgreementDetail((prev) => ({
        ...prev,
        [name]: values,
      }))
    } else {
      setAgreementDetail((prev) => ({
        ...prev,
        [name]: value,
      }))
    }
  }

  // 'WLD' is the pre-existing "World" party used elsewhere in this codebase
  // (national trade-analysis pages default their partner selector to it) —
  // reused here instead of inventing a synthetic value, since it's already
  // a recognized code in dss_economies.
  const handlePartiesChange = (selected: MultiValue<any>) => {
    const selectedValues = selected?.map((s) => s.value) || []

    // 🔥 If nothing selected → reset derived fields
    if (selectedValues.length === 0) {
      setAgreementDetail((prev) => ({
        ...prev,
        iso: [],
        subregion: '',
        crossregional: '',
        parties_escap: '',
        parties_wto: '',
      }))
      setRegion('')
      return
    }

    const {regionArray, subregionArray, crossregional, partiesEscap, partiesWto} =
      deriveFieldsFromParties(selectedValues, countries)

    setAgreementDetail((prev) => ({
      ...prev,
      iso: selectedValues,
      subregion: subregionArray.join('; '),
      crossregional,
      parties_escap: partiesEscap,
      parties_wto: partiesWto,
    }))
    setRegion(regionArray.join('; '))
  }

  // Item 4: selecting "WTO Agreement" as a type is a one-shot convenience
  // fill of Parties (→ World) — it doesn't lock the field, the user can
  // still change parties afterward.
  const handleTypeChange = (selected: MultiValue<any>) => {
    const selectedTypes = selected.map((s) => s.value)
    const newlyAddedWto =
      selectedTypes.includes(WTO_AGREEMENT_TYPE) &&
      !agreementDetail.type?.includes(WTO_AGREEMENT_TYPE)

    if (newlyAddedWto) {
      const {regionArray, subregionArray, crossregional, partiesEscap, partiesWto} =
        deriveFieldsFromParties([WORLD_PARTY], countries)

      setAgreementDetail((prev) => ({
        ...prev,
        type: selectedTypes,
        iso: [WORLD_PARTY],
        subregion: subregionArray.join('; '),
        crossregional,
        parties_escap: partiesEscap,
        parties_wto: partiesWto,
      }))
      setRegion(regionArray.join('; '))
      return
    }

    setAgreementDetail((prev) => ({
      ...prev,
      type: selectedTypes,
    }))
  }

  const validateRequiredFields = () => {
    const requiredFields = [
      'agreement',
      'acronym',
      'type',
      'status',
      'parties_wto',
      'composition',
      'crossregional',
      'wto_rta_id',
    ]

    const isValid =
      requiredFields.every((key) => {
        const val = (agreementDetail as any)[key]
        if (typeof val === 'boolean') return val
        return val !== undefined && val !== null && String(val).trim() !== ''
      }) && region.trim() !== ''

    setIsFormValid(isValid)
  }

  useEffect(() => {
    validateRequiredFields()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agreementDetail, region])

  const serializeSources = (sources: AgreementSource[], legacySource?: any) => {
    // CASE 1: new multi-source UI
    // A source keeps the plain-URL format (no alias|link pipe) unless it has
    // a distinct alias, so untouched legacy single-URL sources round-trip
    // unchanged instead of getting silently rewritten to `url|url` on every
    // save — see agreement #704, which showed a spurious "sources changed"
    // audit-log entry from a save that never touched Sources.
    if (sources.length > 0) {
      return sources
        .filter((s) => s.link)
        .map((s) => (s.alias && s.alias !== s.link ? `${s.alias}|${s.link}` : s.link))
        .join(';')
    }

    // CASE 2: legacy string → upgrade format
    if (typeof legacySource === 'string' && legacySource.trim() !== '') {
      const link = legacySource.trim()
      return `${link}|${link}`
    }

    // CASE 3: empty
    return ''
  }

  const serializeSourcesValidity = (sources: AgreementSource[], legacySource?: any) => {
    if (sources.length > 0) {
      return sources
        .filter((s) => s.link)
        .map((s) =>
          s.is_source_valid !== null && s.is_source_valid !== undefined
            ? String(s.is_source_valid)
            : '0'
        )
        .join(';')
    }

    if (typeof legacySource === 'string' && legacySource.trim() !== '') return '1'
    return ''
  }

  const handleSubmit = () => {
    const formData = new FormData()
    // Dynamically append all agreementDetail fields
    buildAgreementFormEntries(agreementDetail, [
      'xml_file',
      'id',
      'is_new',
      'chapters',
      'pending_chapters',
      'pending_agreement',
      'parties',
      'subregion',
      'sources',
      // These 5 columns still physically exist on dss_trade_agreements_v2
      // (the DROP COLUMN migration is deferred), so GET /:id's SELECT * can
      // leak them back into agreementDetail via the `...detailResdata`
      // spread on load. The backend no longer declares them in its Joi
      // schema, so resubmitting them fails with `"<field>" is not allowed`
      // — skip them unconditionally regardless of whether the column has
      // been dropped.
      'date_of_implementation',
      'region',
      'wits_id',
      'tota_id',
      'aptiad_id',
      'date_xml_created',
      'date_xml_edited',
      'xml_edited_comments',
      'is_sources_valid',
    ]).forEach(([key, value]) => formData.append(key, value))

    const sourcesString = serializeSources(sources, agreementDetail.sources)
    formData.append('sources', sourcesString)

    const sourceValidityString = serializeSourcesValidity(sources, agreementDetail.sources)
    formData.append('is_sources_valid', sourceValidityString)

    // Append file if available
    if (importFile) {
      formData.append('file', importFile)
    }

    // BRA, CHL, EGY, ISR, KOR, MEX, PAR, PRY, PER, PHL, TUN, TUR, URY, SRB, EUN, BGD
    // West Asia; South America; Africa; Middle East; East Asia; North America; Europe
    // Europe; Africa & Middle East; Latin America & Caribbean; Asia Pacific

    confirmDialog(
      `Are you sure you want to ${isEditMode ? 'update' : 'create'} this agreement?`
    ).then((result) => {
      if (!result.isConfirmed) return // exit if user cancels

      setInternalLoading(true)

      if (isEditMode) {
        updateAgreementById(axiosPrivate, idAgreement, formData)
          .then((res) => {
            if (res.status === 200) {
              showSwal({
                title: 'Data updated',
                icon: 'success',
                confirmButtonText: 'OK',
              }).then(() => {
                window.location.reload()
              })
            }
          })
          .catch((err) => {
            console.error(err)
            showSwal('Error', getErrorMessage(err, 'Failed to update data'), 'error')
          })
          .finally(() => {
            setInternalLoading(false)
          })
      } else {
        createAgreement(axiosPrivate, formData)
          .then((res) => {
            if (res.status === 200) {
              showSwal({
                title: 'Data created',
                icon: 'success',
                confirmButtonText: 'OK',
              }).then(() => {
                navigate('/new-rta')
              })
            }
          })
          .catch((err) => {
            console.error(err)
            showSwal('Error', getErrorMessage(err, 'Failed to create data'), 'error')
          })
          .finally(() => {
            setInternalLoading(false)
          })
      }
    })
  }

  const hasChildren = (items: AgreementChapterNew[]): boolean => {
    return items.some(
      (item) => (item.children && item.children.length > 0) || hasChildren(item.children || [])
    )
  }

  const handleCancel = () => {
    confirmDialog('Are you sure you want to cancel?').then((result) => {
      if (result.isConfirmed) {
        if (isAddMode) {
          navigate('/new-rta')
        } else {
          window.location.reload()
        }
      }
    })
  }
  // End of Add or Edit

  // Reorder mode handlers
  const handleEnterReorderMode = () => {
    setReorderChapters(JSON.parse(JSON.stringify(dataTable)))
    setIsReordered(false)
    setIsReorderMode(true)
  }

  const handleReorder = (draggingRow: any, hoveredRow: any) => {
    if (draggingRow && hoveredRow) {
      const draggingRowData = draggingRow.original
      const hoveredRowData = hoveredRow.original

      if (draggingRowData.id === hoveredRowData.id) return

      const dragDepth = draggingRow.depth
      const hoverDepth = hoveredRow.depth

      if (dragDepth === 0 && hoverDepth !== 0) return

      setIsReordered(true)

      if (dragDepth !== 0) {
        if (dragDepth !== hoverDepth && dragDepth !== hoverDepth + 1) {
          return
        }
      }

      const newChapters = [...reorderChapters]

      const updateSortOrder = (nodes: AgreementChapterNew[]) => {
        nodes.forEach((node, index) => {
          node.sort_order = index + 1 // Use 1-based indexing so it aligns with backend

          // if it has children, reset their orders too
          if (node.children && node.children.length > 0) updateSortOrder(node.children)
        })
      }

      const removeNode = (nodes: AgreementChapterNew[], id: string): AgreementChapterNew | null => {
        for (let i = 0; i < nodes.length; i++) {
          if (nodes[i].id === id) {
            return nodes.splice(i, 1)[0]
          }
          if (nodes[i].children) {
            const result = removeNode(nodes[i].children!, id)
            if (result) return result
          }
        }
        return null
      }

      if (dragDepth === 0 && hoverDepth === 0) {
        // Determine direction before removing
        const originalDragIndex = newChapters.findIndex((x) => x.id === draggingRowData.id)
        const originalHoverIndex = newChapters.findIndex((x) => x.id === hoveredRowData.id)
        const isDraggingDown = originalDragIndex < originalHoverIndex

        const movedNode = removeNode(newChapters, draggingRowData.id)
        if (movedNode) {
          const hoveredIndex = newChapters.findIndex((x) => x.id === hoveredRowData.id)
          // When dragging down, insert after the hovered row; when dragging up, insert before
          const insertIndex = isDraggingDown ? hoveredIndex + 1 : hoveredIndex
          newChapters.splice(insertIndex, 0, movedNode)
          updateSortOrder(newChapters)
          setReorderChapters(newChapters)
        }
        return
      }

      if (dragDepth === hoverDepth) {
        // Find the drag index within the same parent to determine direction
        const findIndexInParent = (nodes: AgreementChapterNew[], targetId: string): number => {
          for (const node of nodes) {
            if (node.children) {
              const idx = node.children.findIndex((c) => c.id === targetId)
              if (idx !== -1) return idx
              const deepIdx = findIndexInParent(node.children, targetId)
              if (deepIdx !== -1) return deepIdx
            }
          }
          return -1
        }

        // Find if both items share the same parent and determine direction
        const findSiblingIndices = (
          nodes: AgreementChapterNew[]
        ): {dragIdx: number; hoverIdx: number} | null => {
          for (const node of nodes) {
            if (node.children) {
              const dIdx = node.children.findIndex((c) => c.id === draggingRowData.id)
              const hIdx = node.children.findIndex((c) => c.id === hoveredRowData.id)
              if (dIdx !== -1 && hIdx !== -1) {
                return {dragIdx: dIdx, hoverIdx: hIdx}
              }
              const result = findSiblingIndices(node.children)
              if (result) return result
            }
          }
          return null
        }

        const siblingIndices = findSiblingIndices(newChapters)
        const isDraggingDown = siblingIndices
          ? siblingIndices.dragIdx < siblingIndices.hoverIdx
          : false

        const movedNode = removeNode(newChapters, draggingRowData.id)
        if (movedNode) {
          const findParentAndInsert = (nodes: AgreementChapterNew[]): boolean => {
            for (const node of nodes) {
              if (node.children) {
                const foundIndex = node.children.findIndex((c) => c.id === hoveredRowData.id)
                if (foundIndex !== -1) {
                  movedNode.parent_id = node.id
                  // When dragging down, insert after the hovered row
                  const insertIndex = isDraggingDown ? foundIndex + 1 : foundIndex
                  node.children.splice(insertIndex, 0, movedNode)
                  return true
                }
                if (findParentAndInsert(node.children)) return true
              }
            }
            return false
          }
          findParentAndInsert(newChapters)
          updateSortOrder(newChapters)
          setReorderChapters(newChapters)
        }
      } else if (dragDepth === hoverDepth + 1) {
        const movedNode = removeNode(newChapters, draggingRowData.id)
        if (movedNode) {
          const findTargetAndInsert = (nodes: AgreementChapterNew[]): boolean => {
            for (let i = 0; i < nodes.length; i++) {
              if (nodes[i].id === hoveredRowData.id) {
                if (!nodes[i].children) nodes[i].children = []
                movedNode.parent_id = nodes[i].id
                nodes[i].children!.unshift(movedNode)
                return true
              }
              if (nodes[i].children) {
                if (findTargetAndInsert(nodes[i].children!)) return true
              }
            }
            return false
          }
          findTargetAndInsert(newChapters)
          updateSortOrder(newChapters)
          setReorderChapters(newChapters)
        }
      }
    }
  }

  const flattenReorderUpdates = (chapters: AgreementChapterNew[]) => {
    const flattenedUpdates: {id: string; parent_id: string | null; sort_order: number}[] = []

    const traverse = (nodes: AgreementChapterNew[], parentId: string | null) => {
      nodes.forEach((node, index) => {
        flattenedUpdates.push({
          id: node.id,
          parent_id: parentId,
          sort_order: index + 1,
        })
        if (node.children && node.children.length > 0) {
          traverse(node.children, node.id)
        }
      })
    }

    traverse(chapters, null)
    return flattenedUpdates
  }

  const handleReorderSubmit = () => {
    const flattenedUpdates = flattenReorderUpdates(reorderChapters)

    const body = {rta_id: idAgreement, chapters: flattenedUpdates}

    confirmDialog('Are you sure you want to update the order of this agreement?').then((result) => {
      if (!result.isConfirmed) return

      setInternalLoading(true)

      submitReorderChapter(axiosPrivate, body)
        .then((res) => {
          if (res.status === 200) {
            showSwal({
              title: 'Order updated',
              icon: 'success',
              confirmButtonText: 'OK',
            }).then(() => {
              setIsReorderMode(false)
              setIsReordered(false)
              fetchOptionAgreement()
            })
          }
        })
        .catch((err) => {
          console.error(err)
          showSwal('Error', getErrorMessage(err, 'Failed to update data'), 'error')
        })
        .finally(() => {
          setInternalLoading(false)
        })
    })
  }

  const handleReorderCancel = () => {
    confirmDialog('Are you sure you want to cancel?').then((result) => {
      if (result.isConfirmed) {
        setIsReorderMode(false)
        setIsReordered(false)
        setReorderChapters([])
      }
    })
  }
  // End of Reorder

  const handleExportDetail = async () => {
    if (!idAgreement) return
    try {
      setInternalLoading(true)
      const response = await exportAgreementDetail(axiosPrivate, idAgreement)

      // Trigger browser download
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `agreement-${agreementDetail.acronym || 'detail'}.xlsx`)
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Export failed:', error)
      showSwal('Error', getErrorMessage(error, 'Failed to export agreement detail.'), 'error')
    } finally {
      setInternalLoading(false)
    }
  }

  return (
    <>
      <PageTitle
        breadcrumbs={[
          {
            title: 'Home',
            path: '/',
            isSeparator: false,
            isActive: false,
          },
          {
            title: '',
            path: '',
            isSeparator: true,
            isActive: false,
          },
          {
            title: 'Trade Data Agreement',
            path: 'new-rta',
            isSeparator: false,
            isActive: false,
          },
          {
            title: '',
            path: '',
            isSeparator: true,
            isActive: false,
          },
        ]}
      >
        {isAddMode ? 'Add' : isEditMode ? 'Edit' : agreementDetail.agreement}
      </PageTitle>
      <div className='TradeDataAgreementDetailPage'>
        {!isAddMode &&
          pendingChapters &&
          pendingChapters.length > 0 &&
          ability.can('approve', 'PERM_APPROVE_AGREEMENT') && (
            <div className='alert-no-result-data'>
              <div className='alert alert-warning d-flex align-items-center justify-content-between p-5'>
                <div className='alert-content d-flex align-items-start'>
                  <span className='svg-icon svg-icon-2hx svg-icon-warning me-3'>
                    <i className='fa-solid fa-triangle-exclamation'></i>
                  </span>
                  <div className='d-flex flex-column'>
                    <h5 className='mb-1'>Review Needed</h5>
                    <span>There are chapters that require review or approval:</span>

                    <ul className='mt-2 mb-0'>
                      {pendingChapters.map((chapter: any, index) => (
                        <li key={index}>
                          {ability.can('approve', 'PERM_APPROVE_AGREEMENT') ? (
                            <a className='text-link' href={`/new-rta/review-chapter/${chapter.id}`}>
                              {chapter.title}
                            </a>
                          ) : (
                            <span className='fw-bold'>{chapter.title}</span>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}

        {!isAddMode && pendingAgreement && pendingAgreement.length > 0 && (
          <div className='alert-no-result-data'>
            <div className='alert alert-warning d-flex align-items-center justify-content-between p-5'>
              <div className='alert-content d-flex align-items-start'>
                <span className='svg-icon svg-icon-2hx svg-icon-warning me-3'>
                  <i className='fa-solid fa-triangle-exclamation'></i>
                </span>
                <div className='d-flex flex-column'>
                  <h5 className='mb-1'>Review Needed</h5>
                  {ability.can('approve', 'PERM_APPROVE_AGREEMENT') ? (
                    <span>
                      You have agreement details waiting for your review. Review{' '}
                      <a href={`/new-rta/review-agreement/${pendingAgreement[0].id}`}>here</a>
                    </span>
                  ) : (
                    <span>You have agreement details waiting for review.</span>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        <Row className='mb-5'>
          <Col>
            <Card>
              <Card.Header className='card-header-stretch'>
                <Card.Title className='w-100'>
                  <div className='title-container w-100 d-flex justify-content-between align-items-center'>
                    <div className='title d-flex align-items-center'>
                      <span className='fa fa-file-alt px-2'></span>
                      <h3 className='text-gray-700'>
                        {isDetailMode ? `Detail ${agreementDetail.agreement}` : 'Add New Agreement'}
                      </h3>
                    </div>
                    <div className='d-flex gap-2'>
                      {isDetailMode && (
                        <Button
                          className='btn-audit-log d-flex gap-2 align-items-center'
                          variant='outline-primary'
                          onClick={() => setShowAuditLogModal(true)}
                        >
                          <span className='fa fa-clock-rotate-left'></span>
                          Audit Log
                        </Button>
                      )}
                      {pendingAgreement.length > 0 ||
                      (isDetailMode && !isEditMode && !isReorderMode) ? (
                        <OverlayTrigger
                          placement='top'
                          overlay={
                            isEditDisabled ? (
                              <Tooltip id='edit-disabled-tooltip'>
                                Complete pending reviews for agreements or chapters to enable export
                                data.
                              </Tooltip>
                            ) : (
                              <></>
                            )
                          }
                        >
                          <span className='d-inline-block'>
                            <Button
                              className='d-flex gap-2 align-items-center'
                              variant='outline-primary'
                              onClick={handleExportDetail}
                              disabled={internalLoading || isExportDisabled}
                            >
                              {internalLoading ? (
                                <Spinner animation='border' size='sm' />
                              ) : (
                                <span className='fa fa-file-export'></span>
                              )}
                              Export Detail
                            </Button>
                          </span>
                        </OverlayTrigger>
                      ) : (
                        ''
                      )}
                      {ability.can('update', subject) &&
                      isDetailMode &&
                      !isEditMode &&
                      !isReorderMode ? (
                        <OverlayTrigger
                          placement='top'
                          overlay={
                            isEditDisabled ? (
                              <Tooltip id='edit-disabled-tooltip'>
                                Complete pending reviews for agreements or chapters to enable
                                editing.
                              </Tooltip>
                            ) : (
                              <></>
                            )
                          }
                        >
                          <span className='d-inline-block'>
                            <Button
                              className='btn btn-primary d-flex gap-4 align-items-center'
                              onClick={() => setIsEditMode(true)}
                              disabled={isEditDisabled || internalLoading}
                              style={{pointerEvents: isEditDisabled ? 'none' : 'auto'}} // important
                            >
                              <span className='fa fa-pencil'></span>
                              Edit
                            </Button>
                          </span>
                        </OverlayTrigger>
                      ) : (
                        ''
                      )}
                    </div>
                  </div>
                </Card.Title>
              </Card.Header>
              <Card.Body>
                {!internalLoading ? (
                  <>
                    {(ability.can('update', subject) || ability.can('create', subject)) &&
                    (isAddMode || isEditMode) ? (
                      <Form className='form-agreement'>
                        <div className='row'>
                          <div className='col-12'>
                            <h3>General Information</h3>
                          </div>
                          <div className='col-md-8'>
                            <Form.Label className='text-primary me-0 ps-0' column>
                              <span className='required'>Name</span>
                            </Form.Label>
                            <Form.Control
                              placeholder='Enter full agreement name'
                              type='text'
                              name='agreement'
                              className='required'
                              value={agreementDetail.agreement}
                              onChange={handleAgreementChange}
                            />
                          </div>
                          <div className='col-md-4'>
                            <Form.Label className='text-primary me-0 ps-0' column>
                              <span className='required'>Short Name</span>
                            </Form.Label>
                            <Form.Control
                              type='text'
                              placeholder='Enter short name'
                              name='acronym'
                              className='required'
                              value={agreementDetail.acronym}
                              onChange={handleAgreementChange}
                            />
                          </div>
                          {isEditMode && (
                            <div className='col-md-4'>
                              <Form.Label className='text-primary me-0' column>
                                Treaty Identifier TINA
                              </Form.Label>
                              <Form.Control type='text' value={idAgreement || ''} readOnly />
                            </div>
                          )}
                          <div className='col-md-4'>
                            <Form.Label className='text-primary me-0' column>
                              <span className='required'>Status</span>
                            </Form.Label>
                            <Form.Select
                              name='status'
                              className='required'
                              value={agreementDetail.status}
                              onChange={handleAgreementChange}
                            >
                              <option value='' disabled>
                                Select status
                              </option>
                              {agreementStatus.map((item) => (
                                <option key={item} value={item}>
                                  {item}
                                </option>
                              ))}
                            </Form.Select>
                          </div>
                          <div className='col-md-4'>
                            <Form.Label className='required text-primary me-0' column>
                              WTO RTA Id
                            </Form.Label>
                            <Form.Control
                              type='text'
                              placeholder='Enter WTO RTA Id'
                              name='wto_rta_id'
                              value={agreementDetail.wto_rta_id}
                              onChange={handleAgreementChange}
                            />
                            <a
                              href='https://rtais.wto.org/UI/PublicAllRTAList.aspx'
                              target='_blank'
                              rel='noreferrer'
                              className='text-link'
                            >
                              check the WTO database
                            </a>
                          </div>
                          <div className='col-md-6'>
                            <Form.Label className='text-primary me-0' column>
                              <span className='required'>Type</span>
                            </Form.Label>
                            <div className='resizable-multiselect'>
                              <Select
                                isMulti
                                name='type'
                                placeholder='Select type(s)'
                                options={agreementType} // now [{ value, label }, ...]
                                value={
                                  agreementDetail.type?.map((n: string) => ({
                                    value: n,
                                    label: n,
                                  })) || []
                                }
                                onChange={handleTypeChange}
                                menuPortalTarget={document.body}
                                styles={{
                                  ...multiSelectResizableStyles,
                                  menuPortal: (base) => ({...base, zIndex: 9999}),
                                }}
                              />
                            </div>
                          </div>
                          <div className='col-md-6'>
                            <Form.Label className='text-primary me-0' column>
                              Notification
                            </Form.Label>
                            <div className='resizable-multiselect'>
                              <Select
                                isMulti
                                name='notification'
                                placeholder='Select notification(s)'
                                options={agreementNotification} // now [{ value, label }, ...]
                                value={
                                  agreementDetail.notification?.map((n: string) => ({
                                    value: n,
                                    label: n,
                                  })) || []
                                }
                                onChange={(selected) => {
                                  setAgreementDetail((prev) => ({
                                    ...prev,
                                    notification: selected.map((s) => s.value), // store only the string array
                                  }))
                                }}
                                menuPortalTarget={document.body}
                                styles={{
                                  ...multiSelectResizableStyles,
                                  menuPortal: (base) => ({...base, zIndex: 9999}),
                                }}
                              />
                            </div>
                          </div>
                        </div>
                        <hr />
                        <div className='row'>
                          <div className='col-12'>
                            <h3>Date Information</h3>
                          </div>
                          <div className='col-md-4'>
                            <Form.Label className='text-primary me-0 ps-0' column>
                              Date Signed
                            </Form.Label>
                            <Form.Control
                              type='date'
                              name='date_signed'
                              value={agreementDetail.date_signed}
                              onChange={handleAgreementChange}
                            />
                          </div>
                          <div className='col-md-4'>
                            <Form.Label className='text-primary me-0 ps-0' column>
                              Date Into Force
                            </Form.Label>
                            <Form.Control
                              type='date'
                              name='date_into_force'
                              value={agreementDetail.date_into_force}
                              onChange={handleAgreementChange}
                            />
                          </div>
                          <div className='col-md-4'>
                            <Form.Label className='text-primary me-0 ps-0' column>
                              Date Notification
                            </Form.Label>
                            <Form.Control
                              type='date'
                              name='date_notification'
                              value={agreementDetail.date_notification}
                              onChange={handleAgreementChange}
                            />
                          </div>
                          <div className='col-md-4'>
                            <Form.Label className='text-primary me-0 ps-0' column>
                              End Implementation
                            </Form.Label>
                            <Form.Control
                              type='date'
                              name='end_implementation'
                              value={agreementDetail.end_implementation}
                              onChange={handleAgreementChange}
                            />
                          </div>
                          <div className='col-md-4'>
                            <Form.Label className='text-primary me-0 ps-0' column>
                              Date Inactive
                            </Form.Label>
                            <Form.Control
                              type='date'
                              name='date_inactive'
                              value={agreementDetail.date_inactive}
                              onChange={handleAgreementChange}
                            />
                          </div>
                        </div>
                        <hr />
                        <div className='row'>
                          <div className='col-12'>
                            <h3>Participation & Scope</h3>
                          </div>
                          <div className='col-md-4'>
                            <Form.Label className='text-primary me-0 required' column>
                              Parties
                            </Form.Label>
                            <div className='resizable-multiselect'>
                              <Select
                                isMulti
                                name='notification'
                                placeholder='Select country(s)'
                                options={countries}
                                value={
                                  agreementDetail.iso
                                    ?.map((iso) => countries.find((option) => option.value === iso))
                                    .filter(Boolean) || []
                                }
                                onChange={handlePartiesChange}
                                menuPortalTarget={document.body}
                                styles={{
                                  ...multiSelectResizableStyles,
                                  menuPortal: (base) => ({...base, zIndex: 9999}),
                                }}
                              />
                            </div>
                          </div>
                          <div className='col-md-4'>
                            <Form.Label className='text-primary me-0 ps-0' column>
                              <span className='required'>Composition</span>
                            </Form.Label>
                            <Form.Select
                              name='composition'
                              className='required'
                              value={agreementDetail.composition}
                              onChange={handleAgreementChange}
                            >
                              <option value='' disabled>
                                Select composition
                              </option>
                              {agreementComposition.map((item) => (
                                <option key={item} value={item}>
                                  {item}
                                </option>
                              ))}
                            </Form.Select>
                          </div>
                          <div className='col-md-4'>
                            <Form.Label className='text-primary me-0 ps-0' column>
                              Parties WTO
                            </Form.Label>
                            <Form.Control
                              className='text-capitalize'
                              type='text'
                              name='parties_wto'
                              value={agreementDetail.parties_wto}
                              placeholder='Please select parties'
                              disabled
                            />
                          </div>
                          <div className='col-md-4'>
                            <Form.Label className='text-primary me-0 ps-0' column>
                              <span className='required'>Region</span>
                            </Form.Label>
                            <textarea
                              className='form-control mb-3'
                              rows={2}
                              data-kt-element='input'
                              placeholder='Please select parties'
                              name='region'
                              value={region}
                              disabled
                            ></textarea>
                          </div>
                          <div className='col-md-4'>
                            <Form.Label className='text-primary me-0 ps-0' column>
                              Subregion
                            </Form.Label>
                            <textarea
                              className='form-control mb-3'
                              rows={2}
                              data-kt-element='input'
                              placeholder='Please select parties'
                              name='subregion'
                              value={agreementDetail.subregion}
                              disabled
                            ></textarea>
                          </div>
                          <div className='col-md-4'>
                            <Form.Label className='text-primary me-0 ps-0' column>
                              <span className='required'>Crossregional</span>
                            </Form.Label>
                            <Form.Control
                              type='text'
                              className='text-capitalize'
                              name='crossregional'
                              value={agreementDetail.crossregional}
                              placeholder='Please select parties'
                              disabled
                            />
                          </div>
                          <div className='col-md-4'>
                            <Form.Label className='text-primary me-0 ps-0' column>
                              Parties ESCAP
                            </Form.Label>
                            <Form.Control
                              className='text-capitalize'
                              type='text'
                              name='parties_escap'
                              value={agreementDetail.parties_escap}
                              placeholder='Please select parties'
                              disabled
                            />
                          </div>
                        </div>
                        <hr />
                        <div className='row'>
                          <div className='col-12'>
                            <h3>Others</h3>
                          </div>
                          <div className='col-md-4'>
                            <Form.Label className='text-primary me-0 ps-0' column>
                              Language
                            </Form.Label>
                            <Form.Control
                              type='text'
                              name='language'
                              placeholder='Enter language code (e.g. EN)'
                              value={agreementDetail.language}
                              onChange={handleAgreementChange}
                            />
                          </div>
                          <div className='col-md-4'>
                            <Form.Label className='text-primary me-0 ps-0' column>
                              Service Schedule
                            </Form.Label>
                            <Form.Select
                              name='services_schedules'
                              value={agreementDetail.services_schedules}
                              onChange={handleAgreementChange}
                            >
                              <option value='' disabled>
                                Select service schedule
                              </option>
                              {agreementDetail.services_schedules && (
                                <option value=''>-- Clear selection --</option>
                              )}
                              <option value='yes'>Yes</option>
                              <option value='no'>No</option>
                            </Form.Select>
                          </div>
                          <div className='col-md-4'>
                            <Form.Label className='text-primary me-0 ps-0' column>
                              Desta Id
                            </Form.Label>
                            <Form.Control
                              type='text'
                              name='desta_id'
                              placeholder='Enter Desta Id'
                              value={agreementDetail.desta_id}
                              onChange={handleAgreementChange}
                            />
                          </div>
                        </div>
                        <hr />
                        <div className='row align-items-end'>
                          <div className='col-lg-12 d-flex align-items-center justify-content-between'>
                            <h3>Sources</h3>
                            <Button onClick={() => setShowSourceModal(true)}>Add Source</Button>
                          </div>
                          <div className='col-lg-12'>
                            <DataTable<AgreementSource>
                              data={sources}
                              columns={sourceColumns}
                              enablePagination={false}
                              enableSorting={true}
                              enableGlobalFilter={false}
                              className='my-custom-table-wrapper'
                            />
                          </div>
                        </div>
                        <div className='row pt-5'>
                          <div className='col-lg-12 d-flex justify-content-end gap-3'>
                            <Button variant='default' className='btn-cancel' onClick={handleCancel}>
                              Cancel
                            </Button>
                            <Button
                              variant='primary'
                              onClick={handleSubmit}
                              disabled={internalLoading || !isFormValid}
                            >
                              {internalLoading ? (
                                <>
                                  <Spinner animation='border' size='sm' className='me-2' />
                                  Submit
                                </>
                              ) : (
                                'Submit'
                              )}
                            </Button>
                          </div>
                        </div>
                      </Form>
                    ) : (
                      <AgreementDetailNewView
                        agreementDetail={agreementDetail}
                        idAgreement={idAgreement}
                        region={region}
                      />
                    )}
                  </>
                ) : (
                  <div
                    className='d-flex align-items-center justify-content-center'
                    style={{height: '50vh'}}
                  >
                    <Spinner animation='border' role='status' variant='primary' className='me-3' />
                    <div className='loading-text'>
                      Loading
                      <span className='dot'>.</span>
                      <span className='dot'>.</span>
                      <span className='dot'>.</span>
                    </div>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
        {idAgreement && !isEditMode ? (
          <Row>
            <Col>
              <Card>
                <Card.Header className='card-header-stretch'>
                  <Card.Title className='w-100'>
                    <div className='title-container w-100 d-flex justify-content-between align-items-center'>
                      <div className='title d-flex align-items-center'>
                        <span className='fa fa-file-alt px-2'></span>
                        <h3 className='text-gray-700'>List Content</h3>
                      </div>
                      <div className='header-action'>
                        {isDetailMode && !isEditMode && !isReorderMode ? (
                          <span className='d-inline-block'>
                            <Link
                              to={`/new-rta/${idAgreement}/chapter/add`}
                              className={`btn btn-primary d-flex gap-4 align-items-center ${
                                internalLoading ? 'disabled' : ''
                              }`}
                            >
                              <span className='fa fa-plus'></span>
                              Add
                            </Link>
                          </span>
                        ) : (
                          ''
                        )}
                        {(dataTable.length > 1 || hasChildren(dataTable)) &&
                          ability.can('update', subject) &&
                          !isReorderMode && (
                            <OverlayTrigger
                              placement='top'
                              overlay={
                                pendingAgreement.length > 0 || pendingChapters.length > 0 ? (
                                  <Tooltip id='edit-disabled-tooltip'>
                                    Complete pending reviews for agreements or chapters to enable
                                    editing.
                                  </Tooltip>
                                ) : (
                                  <></>
                                )
                              }
                            >
                              <span className='d-inline-block'>
                                <Button
                                  onClick={handleEnterReorderMode}
                                  variant='outline-primary'
                                  disabled={
                                    pendingAgreement.length > 0 ||
                                    pendingChapters.length > 0 ||
                                    internalLoading
                                  }
                                  style={{
                                    pointerEvents:
                                      pendingAgreement.length > 0 ||
                                      pendingChapters.length > 0 ||
                                      internalLoading
                                        ? 'none'
                                        : 'auto',
                                  }} // important
                                >
                                  Edit Order
                                </Button>
                              </span>
                            </OverlayTrigger>
                          )}
                        {isReorderMode && <p className='text-muted'>Mode: Edit Order</p>}
                      </div>
                    </div>
                  </Card.Title>
                </Card.Header>
                <Card.Body>
                  <ReorderableDataTable
                    data={isReorderMode ? reorderChapters : dataTable}
                    columns={columns}
                    enableRowOrdering={isReorderMode}
                    onReorder={handleReorder}
                    isLoading={internalLoading}
                    isSaving={internalLoading}
                    getRowId={(row) => row.id}
                    getSubRows={(originalRow) => originalRow.children}
                  />
                  {isReorderMode && (
                    <div className='mt-4 d-flex justify-content-end gap-3'>
                      <Button
                        variant='default'
                        className='btn-cancel'
                        onClick={handleReorderCancel}
                      >
                        Cancel
                      </Button>
                      <Button
                        variant='primary'
                        onClick={handleReorderSubmit}
                        disabled={!isReordered || internalLoading}
                      >
                        {internalLoading ? (
                          <>
                            <Spinner animation='border' size='sm' className='me-2' />
                            Saving...
                          </>
                        ) : (
                          'Submit'
                        )}
                      </Button>
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        ) : (
          ''
        )}
      </div>
      <ModalAddSources
        show={showSourceModal}
        onHide={() => {
          setShowSourceModal(false)
          setSourceLink('')
          setSourceAlias('')
        }}
        onSubmit={() => {
          handleAddSource()
          setShowSourceModal(false)
        }}
        isSourceLinkVerified={isSourceLinkVerified}
        alias={sourceAlias}
        link={sourceLink}
        onAliasChange={setSourceAlias}
        onLinkChange={handleSourceLinkChange}
        handleVerifySourceUrl={handleVerifySourceUrl}
      />
      <ModalAuditLog
        show={showAuditLogModal}
        onHide={() => setShowAuditLogModal(false)}
        tableName='dss_trade_agreements_v2'
        recordId={idAgreement}
      />
    </>
  )
}

export default TradeDataAgreementDetailNewPage
